import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getMetalPrices, CachedMetalPrices } from '../../services/metalPriceService';
import { IconWrapper } from '../icons/IconWrapper';
import { FaChartLine } from 'react-icons/fa';

const TickerContainer = styled.div`
  display: flex;
  align-items: center;
  /* background-color: ${({ theme }) => theme.colors.darker}; - Removed for inline integration */
  color: ${({ theme }) => theme.colors.light};
  padding: 0; 
  /* font-size: 0.8rem; - Inherit from parent */
  /* border-bottom: 1px solid ${({ theme }) => theme.colors.dark}; - Removed */
  overflow: hidden;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    /* font-size: 0.7rem; */
    padding: 0;
    justify-content: flex-start; /* Align left on mobile if needed, or center */
    margin-bottom: 0.2rem; /* Add spacing if it stacks on mobile */
  }
`;

const TickerLabel = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  svg {
    margin-right: 0.5rem;
  }
`;

const PriceGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: 0.75rem;
  }
`;

const MetalItem = styled.div`
  display: flex;
  align-items: center;
  
  span.metal-name {
    color: ${({ theme }) => theme.colors.mediumGray};
    margin-right: 0.3rem;
  }

  span.metal-price {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.light};
    font-feature-settings: "tnum";
    font-variant-numeric: tabular-nums;
  }

  span.unit {
    font-size: 0.7em;
    color: ${({ theme }) => theme.colors.darkGray};
    margin-left: 0.2rem;
  }
`;

const LiveDot = styled.span`
  height: 6px;
  width: 6px;
  background-color: #22c55e; // Green for active/live
  border-radius: 50%;
  display: inline-block;
  margin-left: 0.5rem;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
    }
    
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 4px rgba(34, 197, 94, 0);
    }
    
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
    }
  }
`;

const MetalPriceTicker: React.FC = () => {
  const [prices, setPrices] = useState<CachedMetalPrices | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const data = await getMetalPrices();
        if (data) {
          setPrices(data);
        }
      } catch (err) {
        console.error("Failed to load metal ticker", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPrices();
  }, []);

  if (loading || !prices) return null;

  const lastUpdatedTime = new Date(prices.lastUpdated).toLocaleString();

  return (
    <TickerContainer title={`Prices updated: ${lastUpdatedTime}`}>
      <TickerLabel>
        <IconWrapper icon={FaChartLine} size={12} />
        Market Rates <LiveDot />
      </TickerLabel>

      <PriceGroup>
        <MetalItem>
          {/* Copper styling often uses a reddish/orange hue, but we'll stick to theme or neutral */}
          <span className="metal-name">Copper:</span>
          <span className="metal-price">${prices.copper.toFixed(2)}</span>
          <span className="unit">/lb</span>
        </MetalItem>

        <MetalItem>
          <span className="metal-name">Aluminum:</span>
          <span className="metal-price">${prices.aluminum.toFixed(2)}</span>
          <span className="unit">/lb</span>
        </MetalItem>
      </PriceGroup>
    </TickerContainer>
  );
};

export default MetalPriceTicker;
